import { AccountBox, RefreshOutlined, Workspaces } from "@mui/icons-material"
import {
  Skeleton,
  Stack,
  Typography,
  Container,
  Tabs,
  Tab,
  tabClasses,
  tabsClasses,
  Button,
  Box,
  Tooltip,
  Chip,
  Paper,
} from "@mui/material"
import { useMediaQuery } from "@mui/material"
import { Theme } from "@mui/material"
import { useStore } from "@nanostores/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useActiveAddress } from "arweave-wallet-kit"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import { EntityActivity } from "./EntityActivity"
import { EntityAgents } from "./EntityAgents"
import { EntityLP } from "./EntityLP"
import { EntityPortfolio } from "./EntityPortfolio"
import { EntityPortfolioAgents } from "./EntityPortfolioAgents"
import { Subheading } from "../../components/Subheading"
import { SpawnAgentDialog } from "../HomePage/SpawnAgentDialog"
import { getPrimaryName } from "@/api/arns-api"
import { AgentSvg } from "@/components/AgentSvg"
import { BotegaLogoSmall } from "@/components/BotegaLogoSmall"
import { IdBlock } from "@/components/IdBlock"
import { NumberFont } from "@/components/RootLayout/fonts"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import ShineBorder from "@/components/shine-border"
import { HOT_AGENT_PERIOD, HOT_AGENT_REFRESH_INTERVAL, REFRESH_INTERVAL } from "@/settings"
import { $hotOwnersMap } from "@/stores/hot-agents-store"
import { isArweaveId } from "@/utils/arweave"
import { truncateId } from "@/utils/data-utils"

export function UserPage() {
  const { entityId } = useParams()

  const [portfolioValue, setPortfolioValue] = useState<number | null>()
  const [portfolioAgentsValue, setPortfolioAgentsValue] = useState<number | null>(0)
  const [agentsValue, setAgentsValue] = useState<number | null>()
  const [lpValue, setLpValue] = useState<number | null>()

  const validAddress = useMemo(() => isArweaveId(String(entityId)), [entityId])

  if (!entityId || !validAddress) {
    return (
      <Stack component="main" gap={4} paddingY={4}>
        <Typography>Invalid Entity ID.</Typography>
      </Stack>
    )
  }

  const networth = useMemo(() => {
    console.log("onNetworthCalculation", portfolioValue, agentsValue, portfolioAgentsValue, lpValue)
    if (
      typeof portfolioValue !== "number" ||
      typeof agentsValue !== "number" ||
      typeof portfolioAgentsValue !== "number" ||
      typeof lpValue !== "number"
    )
      return null

    return (
      portfolioValue +
      (agentsValue as number) +
      (lpValue as number) +
      (portfolioAgentsValue as number)
    )
  }, [portfolioValue, agentsValue, lpValue, portfolioAgentsValue])

  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    setActiveTab(0)
  }, [entityId])

  const [open, setOpen] = React.useState(false)

  const activeAddress = useActiveAddress()

  const [lastUpdated, setLastUpdated] = useState<number | null>(new Date().getTime())
  const [updatePeriod] = useState(REFRESH_INTERVAL)

  const queryClient = useQueryClient()

  const handleForceRefresh = async (force = false) => {
    console.log("onForceRefresh", document.hasFocus())
    if (!document.hasFocus() && !force) return
    queryClient.invalidateQueries({ queryKey: ["active_portfolio_agents"] })
    queryClient.invalidateQueries({ queryKey: ["agent_info"] })
    queryClient.invalidateQueries({ queryKey: ["token_prices"] })
    queryClient.invalidateQueries({ queryKey: ["existing_balances"] })
    queryClient.invalidateQueries({ queryKey: ["active_orders"] })
    queryClient.invalidateQueries({ queryKey: ["existing_lp_balance"] })
    queryClient.invalidateQueries({ queryKey: ["agent_exit_pnl"] })

    setLastUpdated(new Date().getTime())
  }

  const hotOwners = useStore($hotOwnersMap, {
    keys: [entityId],
  })

  useEffect(() => {
    const lastUpdate = Number(hotOwners[entityId])
    const isHot = !isNaN(lastUpdate) && new Date().getTime() - lastUpdate < HOT_AGENT_PERIOD

    const interval = setInterval(
      () => {
        handleForceRefresh(isHot ? true : false)
      },
      isHot ? HOT_AGENT_REFRESH_INTERVAL : REFRESH_INTERVAL,
    )

    return () => clearInterval(interval)
  }, [REFRESH_INTERVAL, entityId, hotOwners])

  const { data: arnsName } = useQuery({
    queryKey: ["arns_name", entityId],
    queryFn: () => getPrimaryName(entityId),
    enabled: !!entityId,
  })

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  return (
    <>
      <Container maxWidth="lg" disableGutters>
        <Stack gap={3}>
          <Paper sx={{ border: 0, paddingX: 3, paddingY: 2, background: "transparent" }}>
            <Stack
              gap={2}
              sx={{
                marginX: -2,
                borderRadius: 2,
              }}
            >
              <Stack direction={isMobile ? "column" : "row"} flexGrow={1}>
                <Subheading sx={{ marginX: 1 }}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <AccountBox
                      sx={{
                        width: 72,
                        height: 72,
                      }}
                    />
                    <Stack>
                      <Typography variant="h4">
                        <IdBlock
                          label={arnsName ? `${arnsName}.arns` : truncateId(entityId)}
                          value={entityId}
                          href={`https://ao.link/#/entity/${entityId}`}
                          hideCopyToClipboard={isMobile}
                        />
                      </Typography>
                    </Stack>
                    <Tooltip title={`Last refreshed ${dayjs(lastUpdated).format("HH:MM:ss")}`}>
                      <Chip
                        sx={{
                          borderRadius: 0.5,
                          paddingY: 0.5,
                          height: "fit-content",
                        }}
                        label={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <RefreshOutlined fontSize="small" />
                            {isMobile ? "" : `${updatePeriod / 1000}s`}
                          </Stack>
                        }
                        onClick={() => handleForceRefresh()}
                      />
                    </Tooltip>
                  </Stack>
                </Subheading>
                <Stack direction="row" flexGrow={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexGrow={1}
                    justifyContent={isMobile ? "flex-start" : "flex-end"}
                    gap={2}
                  >
                    <Paper
                      sx={{
                        display: "flex",
                        border: 0,
                        paddingX: 4,
                        paddingY: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          fontFamily={NumberFont}
                          variant="h6"
                          sx={{
                            textTransform: "uppercase",
                            fontSize: "1.15rem",
                          }}
                        >
                          Net worth
                        </Typography>
                        {typeof networth !== "number" ? (
                          <Skeleton width={100} />
                        ) : (
                          <Typography
                            variant="h3"
                            sx={{
                              color: "var(--mui-palette-accent-main)",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              fontSize: "1.75rem",
                            }}
                            fontFamily={NumberFont}
                          >
                            <UsdAmountBlock amount={networth} />
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                    {activeAddress === entityId && (
                      <Box sx={{ "& > div": { width: "100%" } }}>
                        <ShineBorder
                          color={"var(--mui-palette-accent-main)"}
                          bgColor="var(--mui-palette-background-default)"
                        >
                          {/* <Tooltip title="Spawn Portfolio Agent"> */}
                          <Button
                            variant="text"
                            fullWidth
                            color="accent"
                            sx={{
                              paddingY: 1,
                              background: "rgba(var(--mui-palette-success-mainChannel) / 0.1)",
                              textTransform: "none",
                              "& img": {
                                width: 47,
                                height: 42,
                                marginRight: isMobile ? 0 : 1,
                              },
                            }}
                            onClick={() => setOpen(true)}
                          >
                            <img src="/dexi_agent_v2.png" alt="Spawn Agent" />

                            <Typography
                              variant={isMobile ? "subtitle2" : "h6"}
                              component="span"
                              color="accent.main"
                            >
                              Spawn Portfolio Agent
                            </Typography>
                          </Button>
                          {/* </Tooltip> */}
                        </ShineBorder>
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                justifyContent={"space-between"}
                paddingX={0.5}
                flexGrow={1}
                gap={2}
                sx={{ overflowX: "auto", minHeight: 70, "& > div": { minWidth: 200 } }}
              >
                <Paper
                  sx={{
                    border: "1px solid var(--mui-palette-divider)",
                    paddingX: 2,
                    paddingY: 2,
                    flexGrow: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    sx={{
                      "& img": {
                        width: 55,
                        height: 50,
                      },
                    }}
                  >
                    <img src="/dexi_agent_v2.png" />
                    <Stack>
                      <Typography variant="body2">Portfolio Agents</Typography>
                      {portfolioAgentsValue === undefined ? (
                        <Skeleton width={100} />
                      ) : (
                        <Typography
                          variant="h6"
                          fontFamily={NumberFont}
                          fontWeight={700}
                          lineHeight={1.2}
                        >
                          <UsdAmountBlock amount={portfolioAgentsValue || 0} />
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
                <Paper
                  sx={{
                    border: "1px solid var(--mui-palette-divider)",
                    paddingX: 2,
                    paddingY: 2,
                    flexGrow: 1,
                  }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <AgentSvg height={40} width={40} />
                    <Stack>
                      <Typography variant="body2">Botega Agents</Typography>
                      {agentsValue === undefined ? (
                        <Skeleton width={100} />
                      ) : (
                        <Typography
                          variant="h6"
                          fontFamily={NumberFont}
                          fontWeight={700}
                          lineHeight={1.2}
                        >
                          <UsdAmountBlock amount={agentsValue || 0} />
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
                <Paper
                  sx={{
                    border: "1px solid var(--mui-palette-divider)",
                    paddingX: 2,
                    paddingY: 2,
                    flexGrow: 1,
                  }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <BotegaLogoSmall height={40} width={40} />
                    <Stack>
                      <Typography variant="body2">Pools</Typography>
                      {lpValue === undefined ? (
                        <Skeleton width={100} />
                      ) : (
                        <Typography
                          variant="h6"
                          fontFamily={NumberFont}
                          fontWeight={700}
                          lineHeight={1.2}
                        >
                          <UsdAmountBlock amount={lpValue || 0} />
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
                <Paper
                  sx={{
                    border: "1px solid var(--mui-palette-divider)",
                    paddingX: 2,
                    paddingY: 2,
                    flexGrow: 1,
                  }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <Workspaces fontSize="large" />
                    <Stack>
                      <Typography variant="body2">Tokens</Typography>
                      {portfolioValue === undefined ? (
                        <Skeleton width={100} />
                      ) : (
                        <Typography
                          variant="h6"
                          fontFamily={NumberFont}
                          fontWeight={700}
                          lineHeight={1.2}
                        >
                          <UsdAmountBlock amount={portfolioValue || 0} />
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Stack>
          </Paper>

          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              marginX: 0,
              [`& .${tabClasses.root}`]: {
                marginX: 0,
                fontSize: "1.5rem",
                fontWeight: `700 !important`,
              },
              [`& .${tabsClasses.indicator}`]: {
                background: "var(--mui-palette-accent-main) !important",
                height: 3,
              },
            }}
          >
            <Tab value={0} label="Overview" />
            <Tab value={1} label="Activity" />
          </Tabs>
          <Stack gap={4} sx={{ display: activeTab === 0 ? "flex" : "none" }}>
            <EntityPortfolio entityId={entityId} onPortfolioValue={setPortfolioValue} />
            <EntityPortfolioAgents
              entityId={entityId}
              onPortfolioAgentsValue={setPortfolioAgentsValue}
            />
            <EntityAgents entityId={entityId} onAgentsValue={setAgentsValue} />
            <EntityLP entityId={entityId} onLpValue={setLpValue} />
          </Stack>
          {activeTab === 1 && <EntityActivity entityId={entityId} />}
        </Stack>
      </Container>
      <SpawnAgentDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}
